import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router-dom";
import { GET_EMPLOYEES, SG_GET_ORGANIZATION_GROUP } from '../../constants/actions'
import { SG_ADD_DEBRIEF_SCHEDULE, SG_GET_SURVEY_STRUCTURES, SG_EMPLOYEE_CATEGORY_SEARCH } from '../../constants/actions'
import { SURVEY_TYPE } from '../../constants/surveys'

// Icons
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
)

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
  </svg>
)

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.7L16.2,16.2Z" />
  </svg>
)

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
  </svg>
)

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
)

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
)

// Timezone options
const timezones = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC/GMT' },
  { value: 'Europe/London', label: 'UK Time (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
]

const ScheduleSurvey = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { addToast } = useToasts()

  // Step management
  const [currentStep, setCurrentStep] = useState(1) // 1 = Survey Details, 2 = Employee Selection

  // Form state
  const [formData, setFormData] = useState({
    surveyType: 'culture_audit',
    timezone: 'America/New_York',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    createPublicLink: false,
    useEmployeeIds: false,
  })

  // Category selection state (from MultipleScheduler.js)
  const [selectedPrimary, setSelectedPrimary] = useState([])
  const [selectedSecondary, setSelectedSecondary] = useState([])

  // Grouping mode
  const [recipientMode, setRecipientMode] = useState('new') // 'new' | 'existing'
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [groupName, setGroupName] = useState('')
  
  // Group search and pagination state
  const [groupSearchTerm, setGroupSearchTerm] = useState('')
  const [groupPage, setGroupPage] = useState(1)
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false)
  
  // Survey scheduling state

  // Recipient selection state (adapted from GroupDetail)
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategories, setSelectedCategories] = useState([])
  const [activeTab, setActiveTab] = useState('individual') // "individual" or "category"
  const [expandedCategory, setExpandedCategory] = useState(null)

  // Bulk category selection state
  const [bulkCategoryId, setBulkCategoryId] = useState(null)
  const [bulkCategoryValueId, setBulkCategoryValueId] = useState(null)

  // Redux state
  const { employees, get_employee_org_category, get_survey_structure, pending, get_auth, organization_groups, groups_pagination, organization_group, group_pending, surveyStructure, employeeCategorySearch } =
    useSelector(
      (state) => ({
        employees: Array.isArray(state.employees?.employees) ? state.employees.employees : [],
        get_employee_org_category: Array.isArray(state.employee_category?.employee_org_category)
          ? state.employee_category.employee_org_category
          : [],
        get_survey_structure: Array.isArray(state.surveystructure?.survey_structure)
          ? state.surveystructure.survey_structure
          : [],
        pending: state.surveys?.pending || false,
        get_auth: state.auth || {},
        organization_groups: state.organization_group?.organization_groups,
        groups_pagination: state.organization_group?.page || {},
        organization_group: state.organization_group?.organization_group || {}, // The newly created/edited group
        group_pending: state.organization_group?.pending || false, // Group operation pending state
        surveyStructure: state.surveystructure?.survey_structure || [],
        employeeCategorySearch: state.employee_category?.search,
      }),
      shallowEqual,
    )
  
  // Fetch survey structure if missing and org id present
  useEffect(() => {
    const orgId = Number(get_auth?.organization_id)
    if (orgId && (!get_survey_structure || get_survey_structure.length === 0)) {
      dispatch({
        type: SG_GET_SURVEY_STRUCTURES,
        payload: `organization=${orgId}`,
      })
    }
  }, [dispatch, get_auth?.organization_id, get_survey_structure])

  // Load required data on mount
  useEffect(() => {
    // Load employees and categories if needed
    if (!employees?.length) {
      dispatch({ type: GET_EMPLOYEES })
    }
    // Add other necessary data loading here
  }, [dispatch, employees?.length])

  // Fetch groups when in existing mode
  useEffect(() => {
    if (recipientMode === 'existing') {
      const searchParam = groupSearchTerm ? `&search=${encodeURIComponent(groupSearchTerm)}` : ''
      const pageParam = `page=${groupPage}`
      const typeParam = 'type=survey'
      dispatch({ 
        type: SG_GET_ORGANIZATION_GROUP, 
        payload: `?${typeParam}&${searchParam}&${pageParam}` 
      })
    }
  }, [dispatch, recipientMode, groupSearchTerm, groupPage])

  // Reset group selection when switching modes
  useEffect(() => {
    if (recipientMode === 'new') {
      setSelectedGroup(null)
      setGroupSearchTerm('')
      setGroupPage(1)
      setIsGroupDropdownOpen(false)
    } else if (recipientMode === 'existing') {
      // Clear category selections when switching to existing mode
      setSelectedPrimary([])
      setSelectedSecondary([])
    }
  }, [recipientMode])

  // Click-away functionality for group dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isGroupDropdownOpen && !event.target.closest('.group-search-container')) {
        setIsGroupDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isGroupDropdownOpen])


  // Build combined categories for the accordion (includes both employee filtering and survey scheduling)
  const accordionCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return []

    const categories = []
    
    // Add primary categories for survey scheduling (always first)
    const primaryCat = get_survey_structure[0].categories.find(c => c.priority === 'primary')
    if (primaryCat) {
      categories.push({
        id: `primary-${primaryCat.id}`,
        name: `${primaryCat.name} (Primary) *`,
        priority: 'primary',
        type: 'survey-scheduling',
        options: primaryCat.options.map(opt => ({
          id: opt.id,
          name: opt.text || opt.name,
          text: opt.text || opt.name,
          name_id: opt.id,
          value: opt.name
        }))
      })
    }
    
    // Add secondary categories for survey scheduling (if exists)
    const secondaryCat = get_survey_structure[0].categories.find(c => c.priority === 'secondary')
    if (secondaryCat) {
      categories.push({
        id: `secondary-${secondaryCat.id}`,
        name: `${secondaryCat.name} (Secondary)`,
        priority: 'secondary', 
        type: 'survey-scheduling',
        options: secondaryCat.options.map(opt => ({
          id: opt.id,
          name: opt.text || opt.name,
          text: opt.text || opt.name,
          name_id: opt.id,
          value: opt.name
        }))
      })
    }
    
    // Add regular employee filtering categories
    const employeeFilteringCats = get_survey_structure[0].categories
      .filter((cat) => cat.visibility !== 'private' && !cat.demographic && cat.priority !== 'primary' && cat.priority !== 'secondary')
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        type: 'employee-filtering',
        options: cat.options.map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }))
    
    return [...categories, ...employeeFilteringCats]
  }, [get_survey_structure])

  // Build primary and secondary category options (from MultipleScheduler.js pattern)
  const primaryCategory = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return []
    
    const primaryCat = get_survey_structure[0].categories.find(c => c.priority === 'primary')
    if (!primaryCat) return []
    
    return primaryCat.options.map(opt => ({
      value: opt.name,
      label: opt.text || opt.name,
      name: opt.text || opt.name,
      name_id: opt.id
    }))
  }, [get_survey_structure])

  const secondaryCategory = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return []
    
    const secondaryCat = get_survey_structure[0].categories.find(c => c.priority === 'secondary')
    if (!secondaryCat) return []
    
    return secondaryCat.options.map(opt => ({
      value: opt.name,
      label: opt.text || opt.name,
      name: opt.text || opt.name,
      name_id: opt.id
    }))
  }, [get_survey_structure])

  // Build filterCategories for bulk dropdowns (similar to GroupDetail's AddEmployeesModal)
  const filterCategories = useMemo(() => {
    // Use get_survey_structure (which is already loaded) instead of surveyStructure
    if (!get_survey_structure?.[0]?.categories) return [];

    return get_survey_structure[0].categories
      .filter((cat) => cat.visibility !== "private" && !cat.demographic)
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        options: (cat.options || []).map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }));
  }, [get_survey_structure]);

  // Derive the currently selected category + value
  const selectedBulkCategory = useMemo(
    () => filterCategories.find((cat) => cat.id === bulkCategoryId) || null,
    [filterCategories, bulkCategoryId]
  );

  const selectedBulkCategoryValue = useMemo(
    () =>
      selectedBulkCategory?.options?.find(
        (opt) => opt.id === bulkCategoryValueId
      ) || null,
    [selectedBulkCategory, bulkCategoryValueId]
  );

  // Derive bulk search loading + data from employeeCategorySearch
  const bulkLoading = !!employeeCategorySearch?.pending;
  const bulkResults = employeeCategorySearch?.results?.filter((result) => result.employee_status === 1) || [];
  const bulkMatchCount = employeeCategorySearch?.results?.filter((result) => result.employee_status === 1)?.length || 0;

  // Filter employees based on search term and selected categories
  const filteredEmployees = useMemo(
    () => {
      if (!Array.isArray(employees)) return []

      let filtered = employees.filter((emp) => emp.status === 1) // Only active employees

      // Combine all selected category IDs (employee filtering + primary + secondary)
      const allSelectedCategoryIds = [
        ...selectedCategories,
        ...selectedPrimary.map(sp => sp.name_id),
        ...selectedSecondary.map(ss => ss.name_id)
      ]

      // If using any categories, filter by categories
      if (allSelectedCategoryIds.length > 0) {
        filtered = filtered.filter((emp) => {
          const empCategories = get_employee_org_category.find(
            (empCat) => empCat.employee === emp.id,
          )

          if (!empCategories?.category) return false

          // Employee must match ALL selected categories
          return allSelectedCategoryIds.every((selectedCatId) => {
            return empCategories.category.some(
              (cat) => cat.value_id === selectedCatId || cat.value === selectedCatId,
            )
          })
        })
      } else if (searchTerm) {
        // Only apply search filter if no categories are selected
        filtered = filtered.filter(
          (emp) =>
            emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      }

      return filtered
    },
    [employees, searchTerm, selectedCategories, selectedPrimary, selectedSecondary, get_employee_org_category],
  )

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Handle employee selection
  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId],
    )
  }

  // Handle category selection (for employee filtering)
  const handleCategoryToggle = (categoryOptionId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryOptionId)
        ? prev.filter((id) => id !== categoryOptionId)
        : [...prev, categoryOptionId],
    )
  }

  // Handle primary/secondary category selection for survey scheduling
  const handleSurveySchedulingCategoryToggle = (category, option, categoryType) => {
    if (categoryType === 'primary') {
      setSelectedPrimary(prev => {
        const isSelected = prev.some(sp => sp.name_id === option.name_id)
        if (isSelected) {
          return prev.filter(sp => sp.name_id !== option.name_id)
        } else {
          return [...prev, {
            value: option.value,
            label: option.name,
            name: option.name,
            name_id: option.name_id
          }]
        }
      })
    } else if (categoryType === 'secondary') {
      setSelectedSecondary(prev => {
        const isSelected = prev.some(ss => ss.name_id === option.name_id)
        if (isSelected) {
          return prev.filter(ss => ss.name_id !== option.name_id)
        } else {
          return [...prev, {
            value: option.value,
            label: option.name,
            name: option.name,
            name_id: option.name_id
          }]
        }
      })
    }
  }

  // Toggle category expand
  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategory((prev) => (prev === categoryId ? null : categoryId))
  }

  // Add employees from category selection
  const addCategoryEmployees = () => {
    const employeeIds = filteredEmployees.map((emp) => emp.id)
    setSelectedEmployees((prev) => [...new Set([...prev, ...employeeIds])])
    // Clear only employee filtering categories, keep primary/secondary for survey structure
    setSelectedCategories([])
    setExpandedCategory(null)
  }

  // Clear all selected employees
  const clearAllEmployees = () => {
    setSelectedEmployees([])
  }

  // Bulk category handlers
  const handleBulkCategoryPreview = () => {
    if (!selectedBulkCategory || !selectedBulkCategoryValue) {
      return;
    }
    dispatch({
      type: SG_EMPLOYEE_CATEGORY_SEARCH,
      payload: {
        page: 1,
        page_size: 50,
        category_name_id: selectedBulkCategory.id,
        category_value_id: selectedBulkCategoryValue.id,
        category_priority: selectedBulkCategory.priority,
        ordering: "employee__first_name",
      },
    });
  };

  const handleBulkCategoryConfirm = () => {
    if (!bulkResults || bulkResults.length === 0) return;

    const newIds = bulkResults
      .map((row) => row.employee_id || row.employee || row.id)
      .filter(Boolean);

    if (!newIds.length) return;

    setSelectedEmployees((prev) => {
      const existing = new Set(prev);
      newIds.forEach((id) => existing.add(id));
      return Array.from(existing);
    });
  };

  // Step navigation
  const goToNextStep = () => {
    if (currentStep === 1) {
      // Validate Step 1 before proceeding
      if (!formData.startDate || !formData.startTime) {
        addToast('Please set a start date and time', { appearance: 'error' })
        return
      }

      if (!formData.endDate || !formData.endTime) {
        addToast('Please set an end date and time', { appearance: 'error' })
        return
      }


      setCurrentStep(2)
    }
  }

  const goToPreviousStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1)
    }
  }

  // Helper to combine date & time into ISO string
  const toLocalDate = (date, time) => {
    if (!date || !time) return null;
    const d = new Date(`${date}T${time}`);
    return d.toISOString();
  };

  const mapTypeOf = (str) => {
    switch (str) {
      case 'culture_audit':
        return SURVEY_TYPE?.AUDIT ?? SURVEY_TYPE?.AUDIT_ASSESSMENT ?? 1
      case 'pulse_survey':
        return SURVEY_TYPE?.PULSE ?? SURVEY_TYPE?.PULSE_SURVEY ?? 2
      case '360_review':
        return SURVEY_TYPE?.REVIEW_360 ?? SURVEY_TYPE?.REVIEW360 ?? 3
      case 'edi_survey':
        return SURVEY_TYPE?.EDI ?? SURVEY_TYPE?.EDI_SURVEY ?? 4
      case 'custom':
      default:
        return SURVEY_TYPE?.AUDIT ?? 1
    }
  }

  // Helper function to create schedule payload (category-based like MultipleScheduler.js)
  const createSchedulePayload = () => {
    const startIso = toLocalDate(formData.startDate, formData.startTime);
    const endIso = toLocalDate(formData.endDate, formData.endTime);
    
    const basePayload = {
      user_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      type_of: mapTypeOf(formData?.surveyType),
      scheduled_for: startIso,
      time_zone: formData.timezone,
      close_at: endIso,
      organization: Number(get_auth?.organization_id),
      survey_structure: get_survey_structure?.[0]?.id || null,
      include_members: !!formData.createPublicLink,
      require_id: !!formData.useEmployeeIds,
    }
    
    // For new groups with category selection, use multiple_list structure
    if (recipientMode === 'new' && selectedPrimary.length > 0) {
      return {
        ...basePayload,
        multiple_list: selectedPrimary.map(sp => [sp.name, sp.name_id]),
        secondary: selectedSecondary?.length > 0 ? selectedSecondary.map(ss => ss.value) : null,
      }
    }
    
    // For existing groups, use the group structure (may need different payload structure)
    if (recipientMode === 'existing' && selectedGroup) {
      return {
        ...basePayload,
        organization_group: selectedGroup.id, // Assuming this is how existing groups are handled
      }
    }
    
    // Fallback for new groups without categories (shouldn't happen due to validation)
    return basePayload
  }

  // Handle form submission (Step 2)
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!formData.startDate || !formData.startTime || !formData.endDate || !formData.endTime) {
      addToast('Please complete the start and end date/time', {
        appearance: 'error',
      })
      return
    }

    if (recipientMode === 'new' && selectedPrimary.length === 0) {
      addToast('Please select at least one primary category', { appearance: 'error' });
      return;
    }

    if (recipientMode === 'existing' && !selectedGroup) {
      addToast('Please select an existing group', { appearance: 'error' });
      return;
    }

    const orgId = Number(get_auth?.organization_id)
    if (!orgId) {
      addToast('Missing organization. Please re-load and try again.', {
        appearance: 'error',
      })
      return
    }

    const startIso = toLocalDate(formData.startDate, formData.startTime);
    const endIso = toLocalDate(formData.endDate, formData.endTime);
    if (new Date(endIso) <= new Date(startIso)) {
      addToast('End date/time must be after start date/time', { appearance: 'error' });
      return;
    }

    try {
      // Create and dispatch survey payload with selected categories
      const surveyPayload = createSchedulePayload();
      
      dispatch({ type: SG_ADD_DEBRIEF_SCHEDULE, payload: surveyPayload });
      
      addToast('Survey scheduled successfully!', { appearance: 'success' });
      navigate('/leader/audit/schedule-v2');
      
    } catch (error) {
      addToast('Error scheduling survey', { appearance: 'error' });
      console.error('Submission error:', error);
    }
  }

  return (
    <Container>
      {/* Header */}
      <HeaderSection>
        <BackButton
          onClick={() => {
            if (currentStep === 1) {
              navigate('/leader/audit/schedule-v2')
            } else {
              goToPreviousStep()
            }
          }}
        >
          <ArrowLeftIcon />
          <span>{currentStep === 1 ? 'Back to Surveys' : 'Back to Details'}</span>
        </BackButton>

        <HeaderContent>
          <IconWrapper>
            <CalendarIcon />
          </IconWrapper>
          <HeaderText>
            <Title>Schedule New Survey</Title>
            <Subtitle>
              {currentStep === 1
                ? 'Step 1: Configure your survey settings'
                : 'Step 2: Select survey recipients'}
            </Subtitle>
          </HeaderText>
        </HeaderContent>

        {/* Progress Indicator */}
        <ProgressIndicator>
          <ProgressStep active={currentStep === 1} completed={currentStep > 1}>
            <ProgressStepNumber active={currentStep === 1} completed={currentStep > 1}>
              1
            </ProgressStepNumber>
            <ProgressStepText>Survey Details</ProgressStepText>
          </ProgressStep>
          <ProgressConnector completed={currentStep > 1} />
          <ProgressStep active={currentStep === 2}>
            <ProgressStepNumber active={currentStep === 2}>2</ProgressStepNumber>
            <ProgressStepText>Select Recipients</ProgressStepText>
          </ProgressStep>
        </ProgressIndicator>
      </HeaderSection>

      {/* Step 1: Survey Details */}
      {currentStep === 1 && (
        <FormContainer>
          <StepFormLayout>
            <StepContentCentered>
              <FormSectionsGrid>
                {/* Left Column */}
                <FormColumn>
                  <PanelSection>
                    <SectionTitle>Survey Configuration</SectionTitle>

                    <FormGroup>
                      <FormLabel>Survey Type</FormLabel>
                      <FormSelect
                        value={formData.surveyType}
                        onChange={(e) => handleFormChange('surveyType', e.target.value)}
                      >
                        <option value="culture_audit">Culture Audit</option>
                        <option value="360_review">360 Review</option>
                        <option value="edi_survey">EDI Survey</option>
                        <option value="pulse_survey">Pulse Survey</option>
                        <option value="custom">Custom Survey</option>
                      </FormSelect>
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Timezone</FormLabel>
                      <FormSelect
                        value={formData.timezone}
                        onChange={(e) => handleFormChange('timezone', e.target.value)}
                      >
                        {timezones.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </FormSelect>
                    </FormGroup>
                  </PanelSection>

                  <PanelSection>
                    <SectionTitle>
                      <LinkIcon />
                      <span>Public Access Options</span>
                    </SectionTitle>

                    <CheckboxGroup>
                      <CheckboxWrapper>
                        <Checkbox
                          type="checkbox"
                          checked={formData.createPublicLink}
                          onChange={(e) => handleFormChange('createPublicLink', e.target.checked)}
                        />
                        <CheckboxLabel>Create public link for survey access</CheckboxLabel>
                      </CheckboxWrapper>
                      <CheckboxHint>
                        Allows respondents to access the survey via a shareable link
                      </CheckboxHint>
                    </CheckboxGroup>

                    {formData.createPublicLink && (
                      <CheckboxGroup>
                        <CheckboxWrapper>
                          <Checkbox
                            type="checkbox"
                            checked={formData.useEmployeeIds}
                            onChange={(e) => handleFormChange('useEmployeeIds', e.target.checked)}
                          />
                          <CheckboxLabel>Use employee IDs as identifiers</CheckboxLabel>
                        </CheckboxWrapper>
                        <CheckboxHint>
                          Requires respondents to enter their employee ID for tracking
                        </CheckboxHint>
                      </CheckboxGroup>
                    )}
                  </PanelSection>
                </FormColumn>

                {/* Right Column */}
                <FormColumn>
                  <PanelSection>
                    <SectionTitle>
                      <ClockIcon />
                      <span>Survey Schedule</span>
                    </SectionTitle>

                    <FormGroup>
                      <FormLabel>Start Date *</FormLabel>
                      <FormInput
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleFormChange('startDate', e.target.value)}
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Start Time *</FormLabel>
                      <FormInput
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => handleFormChange('startTime', e.target.value)}
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>End Date *</FormLabel>
                      <FormInput
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => handleFormChange('endDate', e.target.value)}
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>End Time *</FormLabel>
                      <FormInput
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => handleFormChange('endTime', e.target.value)}
                      />
                    </FormGroup>
                  </PanelSection>
                </FormColumn>
              </FormSectionsGrid>
            </StepContentCentered>

            {/* Step 1 Navigation */}
            <StepNavigationSection>
              <div /> {/* Empty div for spacing */}
              <NextButton onClick={goToNextStep}>Continue to Select Recipients</NextButton>
            </StepNavigationSection>
          </StepFormLayout>
        </FormContainer>
      )}

      {/* Step 2: Employee Selection */}
      {currentStep === 2 && (
        <FormContainer>
          <form onSubmit={handleSubmit}>
            <StepFormLayout>
              <StepContent>
                <PanelSection>
                  <SectionTitle>
                    <UsersIcon />
                    <span>Select Recipients ({selectedEmployees.length} selected)</span>
                  </SectionTitle>

                  {/* Group mode toggle */}
                  <TabContainer>
                    <Tab
                      type="button"
                      active={recipientMode === 'new'}
                      onClick={() => setRecipientMode('new')}
                    >
                      Create New Group
                    </Tab>
                    <Tab
                      type="button"
                      active={recipientMode === 'existing'}
                      onClick={() => setRecipientMode('existing')}
                    >
                      Use Existing Group
                    </Tab>
                  </TabContainer>

                  {recipientMode === 'existing' && (
                    <PanelSection>
                      <FormGroup>
                        <FormLabel>Select Existing Group *</FormLabel>
                        <GroupSearchContainer className="group-search-container">
                          <GroupSearchInputWrapper>
                            <GroupSearchInput
                              type="text"
                              value={groupSearchTerm}
                              onChange={(e) => {
                                setGroupSearchTerm(e.target.value)
                                if (e.target.value.length > 0) {
                                  setIsGroupDropdownOpen(true)
                                }
                              }}
                              onFocus={() => {
                                if (groupSearchTerm.length > 0) {
                                  setIsGroupDropdownOpen(true)
                                }
                              }}
                              placeholder="Search groups by name..."
                            />
                            {!groupSearchTerm && !selectedGroup && (
                              <GroupDropdownButton
                                onClick={() => setIsGroupDropdownOpen(!isGroupDropdownOpen)}
                                type="button"
                              >
                                {isGroupDropdownOpen ? '▲' : '▼'}
                              </GroupDropdownButton>
                            )}
                          </GroupSearchInputWrapper>
                          
                          {selectedGroup && (
                            <SelectedGroupDisplay>
                              <GroupDisplayName>{selectedGroup.name}</GroupDisplayName>
                              <GroupDisplayInfo>
                                {selectedGroup.members_count || 0} members • {selectedGroup.group_type_text || 'Survey'}
                              </GroupDisplayInfo>
                            </SelectedGroupDisplay>
                          )}
                          
                          {recipientMode === 'existing' && (isGroupDropdownOpen || groupSearchTerm) && (
                            <GroupSearchResults>
                              {(() => {
                                const filteredGroups = Array.isArray(organization_groups) 
                                  ? organization_groups.filter(group => 
                                      !groupSearchTerm || 
                                      group.name.toLowerCase().includes(groupSearchTerm.toLowerCase())
                                    )
                                  : [];
                                
                                return filteredGroups.length > 0 ? (
                                  <>
                                    {filteredGroups.map((group) => (
                                      <GroupResultItem
                                        key={group.id}
                                        onClick={() => {
                                          setSelectedGroup(group)
                                          setGroupSearchTerm('')
                                          setIsGroupDropdownOpen(false)
                                        }}
                                        selected={selectedGroup?.id === group.id}
                                      >
                                        <GroupResultName>{group.name}</GroupResultName>
                                        <GroupResultInfo>{group.members_count || 0} members • {group.group_type_text || 'Survey'}</GroupResultInfo>
                                      </GroupResultItem>
                                    ))}
                                  {groups_pagination?.next && (
                                    <LoadMoreButton 
                                      onClick={() => setGroupPage(prev => prev + 1)}
                                    >
                                      Load More Groups
                                    </LoadMoreButton>
                                  )}
                                </>
                                  ) : (
                                    <NoGroupResults>
                                      {groupSearchTerm ? 'No groups found' : 'No groups available'}
                                    </NoGroupResults>
                                  );
                              })()}
                            </GroupSearchResults>
                          )}
                        </GroupSearchContainer>
                      </FormGroup>
                      <CheckboxHint>
                        {selectedGroup 
                          ? `Survey will be sent to ${selectedGroup.members_count || 0} members in "${selectedGroup.name}"`
                          : "Search and select a group to schedule the survey to its existing members."
                        }
                      </CheckboxHint>
                    </PanelSection>
                  )}

                  {/* Tab Navigation */}
                  <TabContainer>
                    <Tab
                      type="button"
                      active={activeTab === 'individual'}
                      onClick={() => {
                        setActiveTab('individual')
                        setSearchTerm('')
                        setSelectedCategories([])
                      }}
                      disabled={recipientMode === "existing"}
                    >
                      Individual Selection
                    </Tab>
                    <Tab
                      type="button"
                      active={activeTab === 'category'}
                      onClick={() => {
                        setActiveTab('category')
                        setSearchTerm('')
                      }}
                      disabled={recipientMode === "existing"}
                    >
                      By Category
                    </Tab>
                  </TabContainer>

                  <EmployeeSelectionLayout>
                    {/* Left: Selection Interface */}
                    <SelectionPanel>
                      {/* Individual Selection Tab */}
                      {activeTab === 'individual' && (
                        <SelectionContent>
                          <SearchContainer>
                            <SearchInputWrapper>
                              <SearchIconWrapper>
                                <SearchIcon />
                              </SearchIconWrapper>
                              <SearchInput
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                disabled={recipientMode === "existing"}
                                placeholder="Search employees..."
                              />
                            </SearchInputWrapper>
                            <SearchHint>
                              {searchTerm
                                ? `${filteredEmployees.length} employee${
                                    filteredEmployees.length !== 1 ? 's' : ''
                                  } found`
                                : `${employees.length} total employees available`}
                            </SearchHint>
                          </SearchContainer>

                          {recipientMode === "existing" ? (
                            <SearchPrompt>
                              <SearchPromptIcon>🔒</SearchPromptIcon>
                              <SearchPromptTitle>
                                Using an existing group
                              </SearchPromptTitle>
                              <SearchPromptText>
                                Employee selection is disabled. Members are taken from the group you specified.
                              </SearchPromptText>
                            </SearchPrompt>
                          ) : searchTerm ? (
                            <EmployeesList>
                              {filteredEmployees.map((emp) => (
                                <EmployeeItem
                                  key={emp.id}
                                  selected={selectedEmployees.includes(emp.id)}
                                  onClick={() => recipientMode !== "existing" && handleEmployeeToggle(emp.id)}
                                >
                                  <EmployeeCheckbox
                                    type="checkbox"
                                    checked={selectedEmployees.includes(emp.id)}
                                    onChange={(e) => {e.stopPropagation(); recipientMode !== "existing" && handleEmployeeToggle(emp.id)}}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <EmployeeDetails>
                                    <EmployeeName>
                                      {emp.first_name} {emp.last_name}
                                    </EmployeeName>
                                    <EmployeeEmail>{emp.email}</EmployeeEmail>
                                  </EmployeeDetails>
                                </EmployeeItem>
                              ))}
                              {filteredEmployees.length === 0 && (
                                <EmptyState>
                                  <EmptyTitle>No employees found</EmptyTitle>
                                  <EmptyText>Try adjusting your search terms</EmptyText>
                                </EmptyState>
                              )}
                            </EmployeesList>
                          ) : (
                            <SearchPrompt>
                              <SearchPromptIcon>👥</SearchPromptIcon>
                              <SearchPromptTitle>Search for employees</SearchPromptTitle>
                              <SearchPromptText>
                                Start typing to find employees by name or email
                              </SearchPromptText>
                            </SearchPrompt>
                          )}
                        </SelectionContent>
                      )}

                      {/* Category Selection Tab */}
                      {activeTab === "category" && (
                        <SelectionContent>
                          <CategoryHeaderRow>
                            <CategoryHeaderTitle>Filter by Tags</CategoryHeaderTitle>
                            <CategoryHeaderSubtitle>
                              Choose a category and value to find matching employees and add them to this survey.
                            </CategoryHeaderSubtitle>
                          </CategoryHeaderRow>
                          {filterCategories.length === 0 ? (
                            <BulkMessageMuted>
                              No tag categories are configured yet. Configure survey tags first, then you can filter by them here.
                            </BulkMessageMuted>
                          ) : (
                            <BulkCategoryBar>
                              <BulkCategorySelectors>
                                <BulkSelect>
                                  <BulkSelectLabel>Category</BulkSelectLabel>
                                  <BulkNativeSelect
                                    value={bulkCategoryId || ""}
                                    onChange={(e) => {
                                      const value = e.target.value || null;
                                      setBulkCategoryId(value ? value : null);
                                      setBulkCategoryValueId(null);
                                    }}
                                  >
                                    <option value="">Select category…</option>
                                    {filterCategories.map((cat) => (
                                      <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </BulkNativeSelect>
                                </BulkSelect>
                                <BulkSelect>
                                  <BulkSelectLabel>Value</BulkSelectLabel>
                                  <BulkNativeSelect
                                    value={bulkCategoryValueId || ""}
                                    onChange={(e) =>
                                      setBulkCategoryValueId(e.target.value || null)
                                    }
                                      disabled={!selectedBulkCategory}
                                    >
                                      <option value="">Select value…</option>
                                      {selectedBulkCategory?.options?.map((opt) => (
                                        <option key={opt.id} value={opt.id}>
                                          {opt.text}
                                        </option>
                                      ))}
                                    </BulkNativeSelect>
                                  </BulkSelect>
                                </BulkCategorySelectors>
                                <BulkCategoryActions>
                                  <CategoryActionButton
                                    type="button"
                                    onClick={handleBulkCategoryPreview}
                                    disabled={!bulkCategoryId || !bulkCategoryValueId || bulkLoading}
                                  >
                                    {bulkLoading ? "Checking..." : "Preview matches"}
                                  </CategoryActionButton>
                                  {bulkMatchCount > 0 && (
                                    <CategoryActionButton
                                      type="button"
                                      onClick={handleBulkCategoryConfirm}
                                      disabled={bulkLoading}
                                    >
                                      Add {bulkMatchCount} employee
                                      {bulkMatchCount !== 1 ? "s" : ""}
                                    </CategoryActionButton>
                                  )}
                                </BulkCategoryActions>
                                {bulkMatchCount === 0 && !bulkLoading && (
                                  <BulkMessageMuted>
                                    Choose a category and value, then click "Preview matches" to see who will be added.
                                  </BulkMessageMuted>
                                )}
                                {bulkMatchCount > 0 && (
                                  <>
                                    <BulkMessage>
                                      Found {bulkMatchCount} matching employee
                                      {bulkMatchCount !== 1 ? "s" : ""}. Showing a sample below.
                                    </BulkMessage>
                                    <BulkSampleList>
                                      {bulkResults.slice(0, 10).map((row) => (
                                        <BulkSampleItem
                                          key={row.id || row.employee_id || row.employee}
                                        >
                                          <BulkSampleName>
                                            {row.first_name} {row.last_name}
                                          </BulkSampleName>
                                          {row.email && (
                                            <BulkSampleEmail>{row.email}</BulkSampleEmail>
                                          )}
                                        </BulkSampleItem>
                                      ))}
                                      {bulkMatchCount > 10 && (
                                        <BulkSampleMore>
                                          + {bulkMatchCount - 10} more not shown
                                        </BulkSampleMore>
                                      )}
                                    </BulkSampleList>
                                  </>
                                )}
                              </BulkCategoryBar>
                          )}
                        </SelectionContent>
                      )}
                    </SelectionPanel>

                    {/* Right: Selected Employees */}
                    <SelectedPanel>
                      <SelectedPanelHeader>
                        <SummaryTitle>
                          Selected Recipients ({selectedEmployees.length})
                        </SummaryTitle>
                        {selectedEmployees.length > 0 && (
                          <ClearButton onClick={clearAllEmployees}>Clear All</ClearButton>
                        )}
                      </SelectedPanelHeader>

                      {recipientMode === "existing" ? (
                        <EmptySelectedState>
                          <EmptySelectedIcon>🔗</EmptySelectedIcon>
                          <EmptySelectedTitle>
                            {selectedGroup 
                              ? `Recipients: ${selectedGroup.name}` 
                              : "No group selected"
                            }
                          </EmptySelectedTitle>
                          <EmptySelectedText>
                            {selectedGroup
                              ? `Survey will be sent to ${selectedGroup.members_count || 0} members in this group`
                              : "Select a group to see recipient details"
                            }
                          </EmptySelectedText>
                        </EmptySelectedState>
                      ) : selectedEmployees.length > 0 ? (
                        <SelectedEmployeesFullList>
                          {selectedEmployees.map((empId) => {
                            const emp = employees.find((e) => e.id === empId)
                            return emp ? (
                              <SelectedEmployeeFullCard key={empId}>
                                <SelectedEmployeeDetails>
                                  <SelectedEmployeeName>
                                    {emp.first_name} {emp.last_name}
                                  </SelectedEmployeeName>
                                  <SelectedEmployeeEmail>{emp.email}</SelectedEmployeeEmail>
                                  {emp.role_name && (
                                    <SelectedEmployeeRole>{emp.role_name}</SelectedEmployeeRole>
                                  )}
                                </SelectedEmployeeDetails>
                                <RemoveButton onClick={() => handleEmployeeToggle(empId)}>
                                  ×
                                </RemoveButton>
                              </SelectedEmployeeFullCard>
                            ) : null
                          })}
                        </SelectedEmployeesFullList>
                      ) : (
                        <EmptySelectedState>
                          <EmptySelectedIcon>👥</EmptySelectedIcon>
                          <EmptySelectedTitle>No recipients selected</EmptySelectedTitle>
                          <EmptySelectedText>
                            Use the selection options on the left to choose survey recipients.
                          </EmptySelectedText>
                        </EmptySelectedState>
                      )}
                    </SelectedPanel>
                  </EmployeeSelectionLayout>

                  {recipientMode === "new" && (
                    <PanelSection>
                      <FormGroup>
                        <FormLabel>Optional Group Name</FormLabel>
                        <FormInput
                          type="text"
                          value={groupName}
                          onChange={(e) => setGroupName(e.target.value)}
                          placeholder='e.g., "Manager Pulse — Sept"'
                        />
                      </FormGroup>
                    </PanelSection>
                  )}
                </PanelSection>
              </StepContent>

              {/* Step 2 Navigation */}
              <StepNavigationSection>
                <BackButton onClick={goToPreviousStep}>
                  <ArrowLeftIcon />
                  <span>Back to Details</span>
                </BackButton>
                <SubmitButton type="submit"
                  disabled={
                    pending ||
                    group_pending ||
                    (recipientMode === "new" && selectedEmployees.length === 0) ||
                    (recipientMode === "new" && selectedPrimary.length === 0) ||
                    (recipientMode === "existing" && !selectedGroup)
                  }
                >
                  {group_pending
                    ? 'Processing...'
                    : recipientMode === "existing"
                    ? `Schedule to ${selectedGroup?.name || "Group"}`
                    : `Schedule Survey (${selectedEmployees.length} recipients)`}
                </SubmitButton>
              </StepNavigationSection>
            </StepFormLayout>
          </form>
        </FormContainer>
      )}
    </Container>
  )
};

export default ScheduleSurvey;

// Styled Components
const Container = styled.div`
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #476dfa;
    color: #476dfa;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const FormLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 600px;
`;

const LeftPanel = styled.div`
  padding: 20px;
  border-right: 1px solid #f0f0f0;
`;

const RightPanel = styled.div`
  padding: 20px;
  background: #fafafa;
`;

const PanelSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const DateTimeGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const CheckboxGroup = styled.div`
  margin-bottom: 12px;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  border-radius: 3px;
`;

const CheckboxLabel = styled.label`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #414141;
`;

const CheckboxHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  margin-left: 24px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  background: #f0f0f0;
  padding: 3px;
  border-radius: 6px;
  margin-bottom: 16px;
  width: fit-content;
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-radius: 4px;
  color: ${props => props.active ? '#414141' : '#7e7e7e'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? 'white' : '#e9ecef'};
  }
`;

const SelectionContent = styled.div`
  min-height: 300px;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 6px;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #7e7e7e;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 8px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 12px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const SearchHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  padding-left: 12px;
`;

const EmployeesList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const EmployeeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f8f8f8;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.selected && `
    background: #f0f4ff;
    border-color: #476dfa;
  `}
`;

const EmployeeCheckbox = styled.input`
  width: 14px;
  height: 14px;
`;

const EmployeeDetails = styled.div`
  flex: 1;
`;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
  margin-bottom: 2px;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const SearchPrompt = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SearchPromptIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const SearchPromptTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const SearchPromptText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const CategoryAddSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const CategoryAddInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #2e7d2e;
`;

const CategoryAddButton = styled.button`
  padding: 6px 12px;
  background: #2e7d2e;
  border: none;
  border-radius: 4px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #256925;
  }
`;

const CategoryFilters = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
`;

const CategoryGroup = styled.div`
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fafafa;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const CategoryGroupLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryCollapseIcon = styled.span`
  font-size: 10px;
  color: #7e7e7e;
  transition: transform 0.2s;
`;

const CategoryName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
`;

const CategoryCount = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const CategoryOptions = styled.div`
  padding: 8px 0;
  background: white;
`;

const CategoryOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 32px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    background: ${props => props.disabled ? 'transparent' : '#f8f9fa'};
  }

  ${props => props.selected && !props.disabled && `
    background: #f0f4ff;
  `}
`;

const CategoryOptionCheckbox = styled.input`
  width: 12px;
  height: 12px;
`;

const CategoryOptionText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #414141;
`;

const SelectedSummary = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
`;

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SummaryTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #414141;
`;

const ClearButton = styled.button`
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #ff4444;
    color: #ff4444;
  }
`;

const SelectedEmployeesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SelectedEmployeeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f0f4ff;
  border: 1px solid #d0d9ff;
  border-radius: 4px;
`;

const SelectedEmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #414141;
`;

const RemoveButton = styled.button`
  width: 16px;
  height: 16px;
  padding: 0;
  background: #ff4444;
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 10px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #cc3333;
  }
`;

const MoreIndicator = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #7e7e7e;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const EmptyTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const EmptyText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const SubmitSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #ccc;
  }
`;

// Progress Indicator Components
const ProgressIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ProgressStepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s;
  
  ${props => {
    if (props.completed) {
      return `
        background: #2e7d2e;
        color: white;
        border: 2px solid #2e7d2e;
      `;
    } else if (props.active) {
      return `
        background: #476dfa;
        color: white;
        border: 2px solid #476dfa;
      `;
    } else {
      return `
        background: #f0f0f0;
        color: #999;
        border: 2px solid #e0e0e0;
      `;
    }
  }}
`;

const ProgressStepText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  color: #7e7e7e;
  text-align: center;
  white-space: nowrap;
`;

const ProgressConnector = styled.div`
  width: 40px;
  height: 2px;
  background: ${props => props.completed ? '#2e7d2e' : '#e0e0e0'};
  transition: background-color 0.2s;
`;

// Step Layout Components
const StepFormLayout = styled.div`
  min-height: 500px;
  display: flex;
  flex-direction: column;
`;

const StepContent = styled.div`
  flex: 1;
  padding: 24px;
`;

const StepContentCentered = styled.div`
  flex: 1;
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
`;

const FormSectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepNavigationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const NextButton = styled.button`
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

// Employee Selection Layout
const EmployeeSelectionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 16px;
  min-height: 500px;
`;

const SelectionPanel = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const SelectedPanel = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SelectedPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
`;

const SelectedEmployeesFullList = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  max-height: 400px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const SelectedEmployeeFullCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    box-shadow: 0 2px 4px rgba(71, 109, 250, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SelectedEmployeeDetails = styled.div`
  flex: 1;
`;

const SelectedEmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  margin-bottom: 2px;
`;

const SelectedEmployeeRole = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #999;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 4px;
`;

const EmptySelectedState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptySelectedIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptySelectedTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const EmptySelectedText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  max-width: 250px;
  line-height: 1.4;
`;

// Group Search Styles
const GroupSearchContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const GroupSearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const GroupSearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const GroupDropdownButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #7e7e7e;
  cursor: pointer;
  padding: 4px;
  font-size: 12px;
  transition: color 0.2s;

  &:hover {
    color: #476dfa;
  }
`;

const SelectedGroupDisplay = styled.div`
  margin-top: 8px;
  padding: 12px;
  background: #f0f4ff;
  border: 1px solid #d0d9ff;
  border-radius: 6px;
`;

const GroupDisplayName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const GroupDisplayInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const GroupSearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
  margin-top: 4px;
`;

const GroupResultItem = styled.div`
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.selected && `
    background: #f0f4ff;
    border-color: #476dfa;
  `}
`;

const GroupResultName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 4px;
`;

const GroupResultInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: #f8f9fa;
  border: none;
  border-top: 1px solid #f0f0f0;
  color: #476dfa;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #e9ecef;
  }
`;

const NoGroupResults = styled.div`
  padding: 16px;
  text-align: center;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

// Category Selection Styles
const MultiSelectWrapper = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
`;

const CategorySelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CategoryCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryCheckbox = styled.input`
  width: 16px;
  height: 16px;
  border-radius: 3px;
`;

const CategoryCheckboxLabel = styled.label`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #414141;
  cursor: pointer;
`;

const EmptyStateText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  text-align: center;
  padding: 16px;
`;

// Category Info Banner Styles
const CategoryInfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f0f4ff;
  border: 1px solid #d0d9ff;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const CategoryInfoIcon = styled.div`
  font-size: 16px;
`;

const CategoryInfoText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #414141;
  line-height: 1.4;
`;

// Bulk Category Dropdown Styles
const CategoryHeaderRow = styled.div`
  margin-bottom: 12px;
`;

const CategoryHeaderTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px;
`;

const CategoryHeaderSubtitle = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const BulkCategoryBar = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #f9fafb;
`;

const BulkCategorySelectors = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
`;

const BulkSelect = styled.div`
  flex: 1;
`;

const BulkSelectLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const BulkNativeSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  background: #ffffff;
`;

const BulkCategoryActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const CategoryActionButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: #2d70e2;
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const BulkMessage = styled.div`
  font-size: 12px;
  color: #374151;
  margin-top: 4px;
`;

const BulkMessageMuted = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`;

const BulkSampleList = styled.div`
  margin-top: 8px;
  border-top: 1px solid #e5e7eb;
  padding-top: 8px;
`;

const BulkSampleItem = styled.div`
  padding: 4px 0;
`;

const BulkSampleName = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const BulkSampleEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const BulkSampleMore = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;